import HelpPopup from '@wandb/weave/common/components/elements/HelpPopup';
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {
  DragDropProvider,
  DragHandle,
  type DragRef,
  DragSource,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import {clsx} from 'clsx';
import {produce} from 'immer';
import _ from 'lodash';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Button, Checkbox, Icon, Input, Popup, Tab} from 'semantic-ui-react';

import {useProjectFieldsQuery} from '../../state/graphql/projectFieldsQuery';
import {
  keyStringToWBMenuOption,
  makeFetchProjectFieldOptions,
} from '../../util/dropdownQueries';
import {type Query} from '../../util/queryTypes';
import * as Run from '../../util/runs';
import {EditableLabel} from '../elements/EditableLabel';
import LabeledOption from '../elements/LabeledOption';
import {
  DEFAULT_GRADIENT,
  GradientPicker,
  type GradientStop,
} from '../GradientPicker';
import {type PCColumn, type PCConfig} from '../PanelParallelCoord/common';
import * as S from '../PanelParallelCoord/PanelParallelCoord.styles';
import ProjectFieldSelector from '../ProjectFieldSelector';

// This file defines the config/settings tab component for the Parallel Coordinates panel.
// Note: The main entry point for the Parallel Coordinates panel is in PanelParallelCoord.tsx

const LEGEND_FIELD_TYPES: string[] = ['string', 'number', 'boolean'];
const LEGEND_FIELD_DEFAULT_KEYS: string[] = [
  'run:displayName',
  'run:name',
  'run:createdAt',
  'run:userName',
];
const LEGEND_FIELDS_FALLBACK: string[] = ['run:displayName'];

type PCColumnRowProps = {
  col: PCColumn;
  defaultKey: string;
  fetchProjectFieldOptions: ReturnType<typeof makeFetchProjectFieldOptions>;
  onDrop: () => void;
  onDragEnter: (col: PCColumn, dragRef: DragRef | null) => void;
  setColumnAccessor: (col: PCColumn, newAccessor: string) => void;
  renameColumn: (col: PCColumn, newName: string) => void;
  toggleColumnAttribute: (col: PCColumn, attribute: 'inverted' | 'log') => void;
  removeColumn: (col: PCColumn) => void;
};

const PCColumnRow: React.FC<PCColumnRowProps> = React.memo(
  ({
    col,
    defaultKey,
    fetchProjectFieldOptions,
    onDrop,
    onDragEnter,
    setColumnAccessor,
    renameColumn,
    toggleColumnAttribute,
    removeColumn,
  }) => {
    const isLog = !!col.log;
    const isInverted = !!col.inverted;
    const accessor = col.accessor ?? defaultKey;
    const accessorOption = keyStringToWBMenuOption(accessor);
    const dragDropID = col.accessor ?? '';

    const partRef = useMemo(() => ({id: dragDropID}), [dragDropID]);

    const handleDragEnter = useCallback(
      ({dragRef}: {dragRef: DragRef | null}) => {
        onDragEnter(col, dragRef);
      },
      [col, onDragEnter]
    );

    const handleSelect = useCallback(
      (v: string | number | undefined) => {
        setColumnAccessor(col, v as string);
      },
      [col, setColumnAccessor]
    );

    const handleRename = useCallback(
      (newName: string) => {
        renameColumn(col, newName);
      },
      [col, renameColumn]
    );

    const handleToggleInverted = useCallback(() => {
      toggleColumnAttribute(col, 'inverted');
    }, [col, toggleColumnAttribute]);

    const handleToggleLog = useCallback(() => {
      toggleColumnAttribute(col, 'log');
    }, [col, toggleColumnAttribute]);

    const handleRemove = useCallback(() => {
      removeColumn(col);
    }, [col, removeColumn]);

    return (
      <DropTarget
        partRef={partRef}
        onDrop={onDrop}
        onDragEnter={handleDragEnter}>
        <DragSource partRef={partRef}>
          <div className="parallel-coordinates-settings__column">
            <DragHandle partRef={partRef}>
              <LegacyWBIcon title="" size="large" name="handle" />
            </DragHandle>
            <div className="parallel-coordinates-settings__column__dimension">
              <Popup
                content={Run.keyStringDisplayName(dragDropID)}
                trigger={
                  <div>
                    <S.StyledSearchableSelect
                      infiniteScroll
                      disableClearable
                      options={fetchProjectFieldOptions}
                      value={accessor}
                      initialOption={accessorOption ?? undefined}
                      onSelect={handleSelect}
                    />
                  </div>
                }
              />
            </div>
            <div className="parallel-coordinates-settings__column__display-name">
              <EditableLabel
                key={col.accessor}
                placeholder="Axis title"
                onSave={handleRename}
                // onBlur={() => this.setState({editingIndex: i})}
                // editing={editingIndex === i}
                serverText={col.displayName}
                // onClick={() => false} // we use the menu option to trigger editing mode, rather than direct click
              />
            </div>
            <Popup
              content="Invert column"
              inverted
              position="top center"
              size="tiny"
              trigger={
                <span>
                  <LegacyWBIcon
                    size="large"
                    title=""
                    name={isInverted ? 'sort-down' : 'sort-up'}
                    className={clsx(`parallel-coordinates-settings__action`, {
                      'parallel-coordinates-settings__action-active':
                        isInverted,
                    })}
                    onClick={handleToggleInverted}
                  />
                </span>
              }
            />
            <Popup
              content="Log scale"
              inverted
              position="top center"
              size="tiny"
              trigger={
                <span>
                  <LegacyWBIcon
                    name="log"
                    size="large"
                    title=""
                    className={clsx(`parallel-coordinates-settings__action`, {
                      'parallel-coordinates-settings__action-active': isLog,
                    })}
                    onClick={handleToggleLog}
                  />
                </span>
              }
            />
            <LegacyWBIcon
              title=""
              className="parallel-coordinates-settings__action"
              size="large"
              name="close"
              onClick={handleRemove}
            />
          </div>
        </DragSource>
      </DropTarget>
    );
  }
);

type PCSettingsProps = {
  config: PCConfig;
  updateConfig: (newConfig: PCConfig) => void;
  pageQuery: Query;
};

export const PCSettings: React.FC<PCSettingsProps> = React.memo(
  ({pageQuery, config, updateConfig}) => {
    const {entityName, projectName} = pageQuery;

    // tempColumns is used for drag+drop column reordering
    const [tempColumns, setTempColumns] = useState<PCColumn[] | undefined>(
      undefined
    );

    const {keys} = useProjectFieldsQuery({
      entityName,
      projectName,
      types: ['string', 'number', 'boolean'],
      columns: ['config', 'summary_metrics'],
      count: 50,
    });

    const renderColumns = useRef<PCColumn[]>(config.columns ?? []);
    const columnAccessors = useMemo(
      // oxlint-disable-next-line react/refs
      () => renderColumns.current.map(c => c.accessor ?? ''),
      // eslint-disable-next-line
      [renderColumns.current]
    );

    const defaultKey = useMemo(
      () => getDefaultKey(keys, columnAccessors),
      [keys, columnAccessors]
    );

    // Save the config and update the renderColumns ref
    const saveConfig = useCallback(
      (newConfig: Partial<PCConfig>) => {
        if (newConfig.columns != null) {
          renderColumns.current = newConfig.columns;
        }
        updateConfig(newConfig);
      },
      [updateConfig]
    );

    // Add a new column
    const addColumn = useCallback(() => {
      const newColumns = [...renderColumns.current, {accessor: defaultKey}];
      saveConfig({
        columns: newColumns,
      });
    }, [defaultKey, saveConfig]);

    // Remove a column
    const removeColumn = useCallback(
      (col: PCColumn) => {
        const newCols = produce(renderColumns.current, draft => {
          const colIndex = _.findIndex(draft, col);
          draft.splice(colIndex, 1);
        });
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Set the column key
    const setColumnAccessor = useCallback(
      (col: PCColumn, newAccessor: string) => {
        const newCols = editColumn(
          renderColumns.current,
          col,
          c => (c.accessor = newAccessor)
        );
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Set the display name for the column
    const renameColumn = useCallback(
      (col: PCColumn, newName: string) => {
        const newCols = editColumn(
          renderColumns.current,
          col,
          c => (c.displayName = newName)
        );
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Toggle "inverted" or "log" attribute on the column
    const toggleColumnAttribute = useCallback(
      (col: PCColumn, attribute: 'inverted' | 'log') => {
        const newCols = editColumn(
          renderColumns.current,
          col,
          c => (c[attribute] = !c[attribute])
        );
        saveConfig({columns: newCols});
      },
      [saveConfig]
    );

    // Move column (drag+drop)
    const onDragEnter = useCallback(
      (col: PCColumn, dragRef: DragRef | null) => {
        if (dragRef == null || dragRef.id === col.accessor) {
          return;
        }
        const cols = tempColumns ?? renderColumns.current;
        const dragColIndex = _.findIndex(cols, {accessor: dragRef.id});
        const dropColIndex = _.findIndex(cols, col);
        if (dragColIndex === -1 || dropColIndex === -1) {
          return;
        }
        const dragCol = cols[dragColIndex];
        const newCols = produce(cols, draft => {
          draft.splice(dragColIndex, 1);
          draft.splice(dropColIndex, 0, dragCol);
        });
        setTempColumns(newCols);
      },
      [tempColumns]
    );

    const onDrop = useCallback(() => {
      if (tempColumns != null) {
        saveConfig({columns: tempColumns});
        setTempColumns(undefined);
      }
    }, [saveConfig, tempColumns]);

    const fetchProjectFieldOptions = useMemo(
      () =>
        makeFetchProjectFieldOptions(entityName, projectName, {
          types: ['string', 'number', 'boolean'],
          filterBy: key => {
            return !_.includes(columnAccessors, key.section + ':' + key.name);
          },
        }),
      [columnAccessors, entityName, projectName]
    );

    const setLegendFields = useCallback(
      (value: string[]) => saveConfig({legendFields: value}),
      [saveConfig]
    );

    const defaultGradient = useMemo(
      () =>
        config.customGradient
          ? {
              type: 'customGradient' as const,
              gradient: config.customGradient,
            }
          : DEFAULT_GRADIENT,
      [config.customGradient]
    );

    const setGradient = useCallback(
      (newGradient: GradientStop[]) => {
        saveConfig({customGradient: newGradient});
      },
      [saveConfig]
    );

    const axesTab = (
      <DragDropProvider>
        <Tab.Pane as="div" className="form-grid">
          {/* oxlint-disable-next-line react/refs */}
          {(tempColumns ?? renderColumns.current).map(col => (
            <PCColumnRow
              key={col.accessor ?? defaultKey}
              col={col}
              defaultKey={defaultKey}
              fetchProjectFieldOptions={fetchProjectFieldOptions}
              onDrop={onDrop}
              onDragEnter={onDragEnter}
              setColumnAccessor={setColumnAccessor}
              renameColumn={renameColumn}
              toggleColumnAttribute={toggleColumnAttribute}
              removeColumn={removeColumn}
            />
          ))}
          <Button
            className="parallel-coordinates-settings__add-column"
            data-test="parallel-coordinates-add-column"
            onClick={addColumn}>
            <Icon title="" name="plus" />
            Add a column
          </Button>
        </Tab.Pane>
      </DragDropProvider>
    );

    const legendTab = (
      <Tab.Pane as="div" className="form-grid">
        <LabeledOption
          label="Title"
          option={
            <Input
              placeholder={''}
              value={config.chartTitle ?? ''}
              onChange={(e, {value}) => {
                saveConfig({
                  chartTitle: value,
                });
              }}
            />
          }
        />
        <p className="chart-label">
          Legend Fields
          <HelpPopup helpText="Set extra variables to show when hovering over chart" />
        </p>
        <ProjectFieldSelector
          className="legend"
          disabled={false}
          selection
          query={pageQuery}
          types={LEGEND_FIELD_TYPES}
          defaultKeys={LEGEND_FIELD_DEFAULT_KEYS}
          fluid
          multi
          value={config.legendFields ?? LEGEND_FIELDS_FALLBACK}
          setValue={setLegendFields}
          searchByKeyAndText
        />
      </Tab.Pane>
    );

    const gradientTab = (
      <Tab.Pane as="div" className="form-grid">
        <div className="chart-label">
          <Checkbox
            label="Color lines based on last column value"
            checked={config.gradientColor ?? true}
            onChange={(e, data) => {
              saveConfig({
                gradientColor: data.checked,
              });
            }}
          />
        </div>
        {(config.gradientColor ?? true) && (
          <GradientPicker
            defaultGradient={defaultGradient}
            setGradient={setGradient}
          />
        )}
      </Tab.Pane>
    );

    const settingsPanes = [
      {
        menuItem: 'Axes',
        render: () => axesTab,
      },
      {
        menuItem: 'Legend',
        render: () => legendTab,
      },
      {
        menuItem: 'Gradient',
        render: () => gradientTab,
      },
    ];

    return (
      <div className="chart-settings parallel-coordinates-settings">
        <Tab
          panes={settingsPanes}
          menu={{
            secondary: true,
            pointing: true,
            className: 'chart-settings-tab-menu',
          }}
        />
      </div>
    );
  }
);

function editColumn(
  allCols: PCColumn[],
  col: PCColumn,
  mutateCol: (c: PCColumn) => void
): PCColumn[] {
  return produce(allCols, draft => {
    const colIndex = _.findIndex(draft, col);
    mutateCol(draft[colIndex]);
  });
}

function getDefaultKey(keys: string[], accessors: string[]): string {
  if (keys.length === 0) {
    return '';
  }

  // Suggest user-defined summary values before wandb summary values, which start with `_`
  const sortedKeys = _.sortBy(keys, key =>
    key.startsWith('summary:_') ? 1 : 0
  );

  const accessorSet: Set<string> = new Set(accessors);

  for (const key of sortedKeys) {
    if (!accessorSet.has(key)) {
      return key;
    }
  }
  return '';
}
